import { Box, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import { EditIcon, TrashIcon } from '@synergy/theme';
import { FormattedMessage, useIntl } from 'react-intl';
import { PaloNextThemeProvider } from '@synergy/theme-palo';
import { PoolButton, PoolMenu, PoolMenuItem } from '@synergy/components';
import { ReactElement, useEffect, useState } from 'react';
import { converter } from '@synergy/utils';
import AddIcon from '@mui/icons-material/AddCircleOutline';

import { InternalNote } from '../../models/internal-note';
import { InternalNoteFormValue, InternalNotesModal } from './internal-notes-modal';
import { NoteType } from '../../models/enums/note-type';
import {
    createInternalNote,
    deleteInternalNote,
    fetchInternalNotes,
    updateInternalNote,
} from '../../actions/internal-notes-actions';
import { getNoteVisibilityTranslationId } from '../../models/enums/note-visibility';
import { internalNotesSelector } from '../../selectors/internal-notes-selectors';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

/**
 * A note as one line of running text: the notes are written in Markdown, and
 * its marks belong in the tooltip, where the note is rendered, not in the cell.
 */
const plainText = (markdown?: string): string =>
    converter
        .makeHtml(markdown ?? '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

export interface InternalNotesProps {
    noteType: NoteType;
    entityId: number;
}

export const InternalNotes = (props: InternalNotesProps): ReactElement => {
    const { noteType, entityId } = props;
    const intl = useIntl();
    const dispatch = useThunkDispatch();
    const internalNotes = useAppSelector(internalNotesSelector);
    const [selectedInternalNote, setSelectedInternalNote] = useState<InternalNote | undefined>(undefined);
    const [showInternalNotesModal, setShowInternalNotesModal] = useState(false);

    useEffect(() => {
        dispatch(fetchInternalNotes(noteType, entityId));
    }, [noteType, entityId, dispatch]);

    const handleEdit = (note: InternalNote) => {
        setSelectedInternalNote(note);
        setShowInternalNotesModal(true);
    };

    const handleDelete = (id: number) => {
        dispatch(deleteInternalNote(id));
    };

    const handleOpenAddNoteModal = () => {
        setSelectedInternalNote(undefined);
        setShowInternalNotesModal(true);
    };

    const handleClose = () => {
        setSelectedInternalNote(undefined);
        setShowInternalNotesModal(false);
    };

    const handleSave = (internalNote: InternalNoteFormValue) => {
        if (internalNote.id) {
            dispatch(
                updateInternalNote({
                    ...internalNote,
                    id: internalNote.id,
                })
            );
        } else {
            dispatch(createInternalNote(internalNote, noteType, entityId));
        }

        handleClose();
    };

    return (
        <>
            <PaloNextThemeProvider>
                {internalNotes && internalNotes.length > 0 && (
                    <Table>
                        <TableHead>
                            <TableRow>
                                {/* No "type" column: it names the kind of record
                                    the notes belong to, so inside one record every
                                    row said the same word — and said it in English
                                    (#2212, R-25). */}
                                <TableCell>
                                    <FormattedMessage id="app.table.column.text" />
                                </TableCell>
                                <TableCell>
                                    <FormattedMessage id="app.table.column.visibility" />
                                </TableCell>
                                <TableCell width="20px" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {internalNotes.map(note => {
                                const visibilities = note.visibility.map(visibility =>
                                    intl.formatMessage({ id: getNoteVisibilityTranslationId(visibility) })
                                );
                                return (
                                    <TableRow key={note.id}>
                                        <TableCell>
                                            {/* The note itself, not an icon one has to
                                                point at to read. Long notes are cut
                                                off at the end of the line and shown
                                                whole in the tooltip. */}
                                            <Tooltip
                                                title={
                                                    <Box
                                                        color="grey[800]"
                                                        dangerouslySetInnerHTML={{
                                                            __html: converter.makeHtml(note.text ?? ''),
                                                        }}
                                                    />
                                                }
                                            >
                                                <Box
                                                    sx={{
                                                        maxWidth: 560,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {plainText(note.text)}
                                                </Box>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>{visibilities.join(', ')}</TableCell>
                                        <TableCell width="20px">
                                            <PoolMenu menuPosition="right">
                                                <PoolMenuItem
                                                    title={<FormattedMessage id="app.common.edit" />}
                                                    isExtendedMenu={true}
                                                    icon={<EditIcon />}
                                                    onClick={() => handleEdit(note)}
                                                />
                                                <PoolMenuItem
                                                    title={<FormattedMessage id="app.common.delete" />}
                                                    isExtendedMenu={true}
                                                    icon={<TrashIcon />}
                                                    onClick={() => handleDelete(note.id)}
                                                />
                                            </PoolMenu>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </PaloNextThemeProvider>

            <Box sx={{ ml: 1.5, mt: 2 }}>
                <PoolButton
                    styleType="thirdLevel"
                    startIcon={<AddIcon color="primary" />}
                    onClick={() => handleOpenAddNoteModal()}
                >
                    <FormattedMessage id="app.internalNotes.add" />
                </PoolButton>
            </Box>

            <InternalNotesModal
                internalNote={selectedInternalNote}
                open={showInternalNotesModal}
                onClose={handleClose}
                onSave={handleSave}
            />
        </>
    );
};
